/**
 * Auth overlay host.
 * Set1 classic login/register: no handle, no title header, no close button (backdrop dismiss only).
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, type ViewStyle, useWindowDimensions } from 'react-native';
import { useSharedValue, withTiming } from 'react-native-reanimated';
import { appTheme, useThemeColors } from '../shared/theme';
import { createBottomSheetChromeStyles } from '../shared/theme/authSheetStyles';
import { useAuthSheets, type AuthSheetState } from '../shared/auth/authSheetContext';
import { useAuthSheetOtpChrome, AUTH_CENTER_BOTTOM_INSET_FRACTION } from '../shared/auth';
import { useTranslation } from '../shared/i18n';
import { ConfigurableOverlay } from '../components/common/ConfigurableOverlay';
import { Clickable2 } from '../components/common/Clickable2';
import { SvgIcon } from '../components/common/SvgIcon';
import { LoginSheetContent, RegisterSheetContent } from './variants/classic';
import { MainScreenAuthForms } from './variants/hero_dock';
import { OTPVerificationSheetContent } from './OTPVerificationSheetContent';
import { ResetPasswordSheetContent } from './ResetPasswordScreen';
import { authSheetRequiresSequencedTransition, authSheetStateEqual, getAuthOverlayMotionForState, getAuthSheetPresentation } from '../shared/auth/authOverlayPresentation';
import { ONBOARD_EASING, ONBOARD_TRANSITION_MS } from '../shared/theme/studio/onboardingMotion';

/** Full-window layer above `AppInitializer`; `box-none` so the navigator receives touches when no overlay is up. */
const auth_host_layer: ViewStyle = {
  ...StyleSheet.absoluteFillObject,
  zIndex: 1000,
  elevation: 24,
};

function AuthSheetCloseButton({
  onPress,
  accessibilityLabel,
  colors,
  chrome,
}: {
  onPress: () => void;
  accessibilityLabel: string;
  colors: { text: string };
  chrome: ReturnType<typeof createBottomSheetChromeStyles>;
}) {
  return (
    <Clickable2
      testID="signin-close"
      onPress={onPress}
      haptics
      hapticType="selection"
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      style={chrome.closeButton}>
      <View style={chrome.closeButtonIconWrap} pointerEvents="none">
        <SvgIcon name="x" size={18} color={colors.text} />
      </View>
    </Clickable2>
  );
}

export function AuthSheetHost() {
  const { t } = useTranslation();
  const colors = useThemeColors() ?? appTheme.colors;
  const chrome = useMemo(() => createBottomSheetChromeStyles(colors), [colors]);
  const { state, dismiss: contextDismiss, openLogin, openRegister } = useAuthSheets();
  const { height: window_h } = useWindowDimensions();
  const [displayed, setDisplayed] = useState<AuthSheetState | null>(null);
  const [overlayVisible, setOverlayVisible] = useState(false);
  /** Overlap-only keyboard lift for the default (center-card) presentation — never full keyboard height. */
  const [panel_keyboard_nudge, setPanelKeyboardNudge] = useState(0);

  const main_screen_swap = useSharedValue(0);
  const prev_main_auth_kind = useRef<'login' | 'register' | null>(null);
  const main_screen_scroll_max_h = useMemo(() => Math.round(window_h * 0.5), [window_h]);
  const is_main_screen_sheet = useMemo(
    () => displayed != null && getAuthSheetPresentation(displayed) === 'mainScreen' && (displayed.kind === 'login' || displayed.kind === 'register'),
    [displayed],
  );

  useLayoutEffect(() => {
    if (displayed == null) {
      prev_main_auth_kind.current = null;
      return;
    }
    if (getAuthSheetPresentation(displayed) !== 'mainScreen' || (displayed.kind !== 'login' && displayed.kind !== 'register')) {
      prev_main_auth_kind.current = null;
      return;
    }
    const target = displayed.kind === 'register' ? 1 : 0;
    const prev = prev_main_auth_kind.current;
    if (prev == null) {
      main_screen_swap.value = target;
    } else if (prev !== displayed.kind) {
      main_screen_swap.value = withTiming(target, { duration: ONBOARD_TRANSITION_MS, easing: ONBOARD_EASING });
    }
    prev_main_auth_kind.current = displayed.kind;
  }, [displayed, main_screen_swap]);

  const motion = useMemo(() => getAuthOverlayMotionForState(displayed), [displayed]);

  const panel_style = useMemo((): ViewStyle => {
    const flat = StyleSheet.flatten(chrome.panel) as ViewStyle;
    if (is_main_screen_sheet) {
      const { backgroundColor: _bg, ...rest } = flat;
      return { ...rest };
    }
    const { maxHeight: _mh, borderTopLeftRadius: _tl, borderTopRightRadius: _tr, ...center_rest } = flat;
    return {
      ...center_rest,
      backgroundColor: colors.surface,
      borderRadius: motion.panelBorderRadius ?? appTheme.borderRadius.xl,
    };
  }, [chrome.panel, colors.surface, is_main_screen_sheet, motion.panelBorderRadius]);

  const pendingSwapRef = useRef<AuthSheetState | null>(null);
  const previous_sheet_ref = useRef<AuthSheetState | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const { dismissFromChrome, onOverlayRequestClose, otp_dismiss_blocked, show_otp_close_button, handleOtpAbortSafeExit } = useAuthSheetOtpChrome(
    state,
    displayed,
    contextDismiss,
  );

  useEffect(() => {
    const s = state;
    const d = displayed;

    if (s == null) {
      pendingSwapRef.current = null;
      setPanelKeyboardNudge(0);
      if (d != null) {
        setOverlayVisible(false);
      }
      return;
    }

    if (d != null && s != null && authSheetStateEqual(d, s)) {
      if (!overlayVisible) {
        setOverlayVisible(true);
      }
      return;
    }

    if (d == null) {
      setDisplayed(s);
      setOverlayVisible(true);
      return;
    }

    if (!authSheetRequiresSequencedTransition(d, s)) {
      setPanelKeyboardNudge(0);
      setDisplayed(s);
      return;
    }

    previous_sheet_ref.current = d;
    pendingSwapRef.current = s;
    setOverlayVisible(false);
  }, [state, displayed]);

  const onOverlayCloseAnimationComplete = useCallback(() => {
    const pending = pendingSwapRef.current;
    pendingSwapRef.current = null;
    setPanelKeyboardNudge(0);

    if (pending != null) {
      setDisplayed(pending);
      setOverlayVisible(true);
      return;
    }

    if (stateRef.current == null) {
      setDisplayed(null);
    }
  }, []);

  const on_keyboard_nudge = useCallback((offset_px: number) => {
    setPanelKeyboardNudge(offset_px > 0 ? Math.ceil(offset_px) : 0);
  }, []);

  useEffect(() => {
    setPanelKeyboardNudge(0);
  }, [displayed?.kind]);

  const sheetKind = displayed?.kind;
  /** Center-card login/register (set1 classic): zero chrome — no handle, title, or close. */
  const is_center_credential =
    !is_main_screen_sheet && (sheetKind === 'login' || sheetKind === 'register');
  /** OTP/reset only — never login/register. */
  const show_floating_close =
    !is_main_screen_sheet &&
    sheetKind !== 'login' &&
    sheetKind !== 'register' &&
    (sheetKind === 'reset' || (sheetKind === 'otp' && show_otp_close_button));
  const show_handle = is_main_screen_sheet ? true : false;
  const dock_header_title =
    sheetKind === 'login' ? t('auth.signIn') : sheetKind === 'register' ? t('auth.signUp') : '';

  const needs_configurable_overlay = displayed != null;

  const body_wrap_style = useMemo((): ViewStyle => {
    if (motion.shrinkWrapPanel) {
      return {
        width: '100%',
        flexGrow: 0,
        flexShrink: 1,
        position: 'relative',
        // Floating close only (otp/reset). Login/register: no reserved top band.
        ...(show_floating_close ? { paddingTop: appTheme.spacing.md } : null),
      };
    }
    return { flex: 1, minHeight: 0, width: '100%', position: 'relative' };
  }, [motion.shrinkWrapPanel, show_floating_close]);

  if (displayed == null && !overlayVisible) {
    return null;
  }

  const close_label = t('common.close');

  const overlay_body = (
    <>
      {show_handle ? <View style={chrome.handle} /> : null}
      {/* Title + close header: hero_dock mainScreen ONLY — never set1 classic credentials. */}
      {is_main_screen_sheet ? (
        <View style={chrome.headerRow}>
          <View style={chrome.headerSpacer} />
          {dock_header_title ? (
            <Text style={chrome.headerTitle} numberOfLines={1}>
              {dock_header_title}
            </Text>
          ) : (
            <View style={chrome.headerFlexFill} />
          )}
          <AuthSheetCloseButton onPress={dismissFromChrome} accessibilityLabel={close_label} colors={colors} chrome={chrome} />
        </View>
      ) : null}

      {is_main_screen_sheet ? (
        <View style={{ width: '100%', flexGrow: 0, flexShrink: 1, minHeight: 0, alignItems: 'stretch' }}>
          <MainScreenAuthForms
            swap={main_screen_swap}
            onDismiss={dismissFromChrome}
            registerSubData={displayed?.kind === 'register' ? displayed.subData : undefined}
            mainScreenScrollMaxHeight={main_screen_scroll_max_h}
            onSwitchToRegister={() => openRegister({ presentation: 'mainScreen' })}
            onSwitchToLogin={() => openLogin({ presentation: 'mainScreen' })}
            formMotionVariant="mainScreenV3"
            onKeyboardPanelNudge={on_keyboard_nudge}
          />
        </View>
      ) : (
        <View style={body_wrap_style}>
          {displayed?.kind === 'login' ? <LoginSheetContent onDismiss={contextDismiss} onKeyboardPanelNudge={on_keyboard_nudge} /> : null}
          {displayed?.kind === 'register' ? <RegisterSheetContent onDismiss={contextDismiss} subData={displayed.subData} onKeyboardPanelNudge={on_keyboard_nudge} /> : null}
          {displayed?.kind === 'otp' ? (
            <OTPVerificationSheetContent onDismiss={contextDismiss} routeParams={displayed.params} onAbortSafeExit={handleOtpAbortSafeExit} onKeyboardPanelNudge={on_keyboard_nudge} />
          ) : null}
          {displayed?.kind === 'reset' ? <ResetPasswordSheetContent onDismiss={contextDismiss} routeParams={displayed.params} onKeyboardPanelNudge={on_keyboard_nudge} /> : null}

          {/* Never mount a close control for login/register. */}
          {show_floating_close && !is_center_credential ? (
            <View style={chrome.closeButtonFloating} pointerEvents="box-none">
              <AuthSheetCloseButton onPress={dismissFromChrome} accessibilityLabel={close_label} colors={colors} chrome={chrome} />
            </View>
          ) : null}
        </View>
      )}
    </>
  );

  return (
    <View style={auth_host_layer} pointerEvents="box-none" collapsable={false}>
      {needs_configurable_overlay ? (
        <ConfigurableOverlay
          visible={overlayVisible}
          onRequestClose={onOverlayRequestClose}
          allowDismiss={!otp_dismiss_blocked}
          onCloseAnimationComplete={onOverlayCloseAnimationComplete}
          placement={motion.placement}
          widthFraction={motion.widthFraction}
          maxHeightFraction={is_main_screen_sheet ? undefined : motion.maxHeightFraction ?? 0.86}
          enterPreset={motion.enterPreset}
          exitPreset={motion.exitPreset}
          openDurationMs={motion.openDurationMs}
          closeDurationMs={motion.closeDurationMs}
          hiddenSlideOffset={motion.hiddenSlideOffset}
          panelMaxWidth={motion.panelMaxWidth}
          backdropOpacityMax={motion.backdropOpacityMax ?? 1}
          backdropAccessibilityLabel={close_label}
          panelStyle={panel_style}
          padBottomSafeArea={is_main_screen_sheet}
          shrinkWrapPanel={is_main_screen_sheet || Boolean(motion.shrinkWrapPanel)}
          stripAndroidPanelFill={is_main_screen_sheet}
          panelLiftPx={panel_keyboard_nudge}
          centerBottomInsetFraction={AUTH_CENTER_BOTTOM_INSET_FRACTION}>
          {overlay_body}
        </ConfigurableOverlay>
      ) : null}
    </View>
  );
}
